/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Table } from 'antd';
import { FaEye, FaEyeSlash, FaPen, FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm } from '../../../util/Alert';
import { FormProvider, useForm } from 'react-hook-form';
import 'dayjs/locale/th';
import { useHistory } from 'react-router';
import fitSurveyServices from '../../../../services/fit-survey.services';
import LegacySurveyModal from '../components/modal/LegacySurveyModal';
import {
  legacySurveyFormDefaultValues,
  legacySurveyFormSchema,
} from '../components/dto/legacy-survey-form.dto';

dayjs.locale('th');

const pageSize = 10;

const LegacySurveyPage = () => {
  const [legacySurveyFormOpen, setLegacySurveyFormOpen] = useState(false);
  const {
    data: legacySurveyLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();
  const history = useHistory();

  const legacySurveyForm = useForm({
    defaultValues: legacySurveyFormDefaultValues,
    resolver: legacySurveyFormSchema(),
  });

  const _HandleLegacySurveyFormOpen = (formType) => {
    legacySurveyForm.setValue('formType', formType);
    setLegacySurveyFormOpen(true);
  };

  const _HandleLegacySurveyFormClose = () => {
    legacySurveyForm.reset(legacySurveyFormDefaultValues);
    setLegacySurveyFormOpen(false);
  };

  fitSurveyServices.useQueryGetLegacySurveyLists(
    { take: pageSize, page },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    }
  );

  const { mutate: deleteLegacySurvey } =
    fitSurveyServices.useMutationDeleteLegacySurvey(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey'],
        refetchType: 'all',
      });
    });

  const { mutate: updateLegacySurveyDisplay } =
    fitSurveyServices.useMutationUpdateLegacySurveyDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-legacy-survey'],
        refetchType: 'all',
      });
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รูปภาพ',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (_, record) => (
        <div>
          <img
            alt={record.title}
            src={record.imageUrl}
            style={{
              width: 200,
              height: 100,
            }}
          />
        </div>
      ),
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <span>{record.title}</span>,
    },
    {
      title: 'คำอธิบาย',
      dataIndex: 'description',
      key: 'description',
      render: (_, record) => <span>{record.description}</span>,
    },
    {
      title: 'ประเภทนักธุรกิจ',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (_, record) => (
        <span>
          {record.memberType === 'all' && 'ทั้งหมด'}
          {record.memberType === 'Distributor' && 'เฉพาะนักธุรกิจ'}
          {record.memberType === 'Member' && 'เฉพาะ VIP'}
        </span>
      ),
    },
    {
      title: 'วันที่เริ่ม',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (_, record) => (
        <span>{dayjs(record.startDate).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.startDate).diff(dayjs(b.startDate), 'D') > 0 ? 1 : -1,
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (_, record) => (
        <span>{dayjs(record.endDate).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.endDate).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: 'การมองเห็น',
      dataIndex: 'display',
      key: 'display',
      render: (_, record) => <span>{record.display ? 'เปิด' : 'ปิด'}</span>,
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MMM/YYYY')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyDisplay({
                    id: record.id,
                    display: false,
                  });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงหมวดหมู่คำถาม');
                if (confirm)
                  updateLegacySurveyDisplay({
                    id: record.id,
                    display: true,
                  });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              legacySurveyForm.setValue('id', record.id);
              _HandleLegacySurveyFormOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteLegacySurvey({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>แบบสอบถาม</PanelHeader>
      <PanelBody>
        <div style={{ display: 'flex', alignItems: 'center' }} className="mb-2">
          <div style={{ flex: 1 }} />
          <Button
            onClick={() => {
              _HandleLegacySurveyFormOpen('create');
            }}
            type="primary"
          >
            <FaPlus className="mr-2" />
            เพิ่มแบบสอบถาม
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={legacySurveyLists}
          style={{ backgroundColor: '#fff' }}
          rowKey="id"
          onChange={({ current }) => {
            setPage(current);
          }}
          pagination={{
            pageSize,
            total,
          }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                history.push(`/questionaire/survey/${record.id}`);
              },
            };
          }}
        />
      </PanelBody>
      <FormProvider {...legacySurveyForm}>
        <LegacySurveyModal
          open={legacySurveyFormOpen}
          handleClose={_HandleLegacySurveyFormClose}
        />
      </FormProvider>
    </Panel>
  );
};

export default LegacySurveyPage;
